import React, { useEffect, useMemo, useState, Children } from 'react';
import { dashboardSeed } from '../lib/data';
import { ActivityIcon, TrendingUpIcon, PercentIcon, DollarSignIcon, ClockIcon, BarChart3Icon, CheckCircleIcon, ZapIcon, FileTextIcon } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
export function Dashboard() {
  const [data, setData] = useState(dashboardSeed);
  const [now, setNow] = useState(new Date());
  // Set page title
  useEffect(() => {
    document.title = 'Mint | Dashboard';
  }, []);
  // clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  // gentle live ticks (no blinking)
  useEffect(() => {
    const t = setInterval(() => {
      setData(prev => ({
        ...prev,
        agents: prev.agents.map((a, i) => {
          // small bounded jitter per agent
          const jitter = i === 0 ? 2 : 1;
          const ips = Math.max(1, a.ips + Math.round((Math.random() - 0.5) * 2 * jitter));
          const processed = a.processed + Math.max(1, Math.round(ips * 0.6));
          return {
            ...a,
            ips,
            processed
          };
        })
      }));
    }, 1500);
    return () => clearInterval(t);
  }, []);
  const hhmmss = useMemo(() => {
    // Add 3 hours to get East Coast time
    const estTime = new Date(now.getTime() + 3 * 60 * 60 * 1000);
    return estTime.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }, [now]);
  // helpers
  const fmtInt = (n: number) => n.toLocaleString();
  const fmtUSD = (n: number) => `$${n.toLocaleString(undefined, {
    maximumFractionDigits: 1
  })}`;
  // Generate sparkline data
  const generateSparklineData = (points: number = 20) => {
    const data = [];
    for (let i = 0; i < points; i++) {
      data.push(20 + Math.random() * 10 - i * 0.2);
    }
    return data;
  };
  const sparklineData = useMemo(() => generateSparklineData(), []);
  const Sparkline = ({
    data
  }: {
    data: number[];
  }) => {
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min;
    const points = data.map((value, index) => {
      const x = index / (data.length - 1) * 100;
      const y = 30 - (value - min) / range * 28;
      return `${x},${y}`;
    }).join(' ');
    return <svg className="w-full h-8 mt-2" viewBox="0 0 100 30" data-id="element-1065">
        <polyline points={points} fill="none" stroke="#6b7280" strokeWidth="1.5" data-id="element-1066" />
      </svg>;
  };
  return <div className="min-h-screen bg-gray-50 px-6 py-6" data-id="element-1067">
      {/* Header */}
      <div className="flex items-center justify-between mb-8" data-id="element-1068">
        <h1 className="text-3xl font-semibold text-gray-900" data-id="element-1069">Dashboard</h1>
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm" data-id="element-1070">
          <div className="relative" data-id="element-1071">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" data-id="element-1072" />
            <span className="absolute inset-0 h-2 w-2 rounded-full bg-emerald-500 animate-ping" data-id="element-1073" />
          </div>
          <span className="text-sm text-gray-700" data-id="element-1074">Live, {hhmmss} EST</span>
        </div>
      </div>
      {/* Validation Funnel */}
      <div className="mb-6" data-id="element-1075">
        <h2 className="text-lg font-medium text-gray-900 mb-3" data-id="element-1076">
          Validation Funnel
        </h2>
        <div className="grid grid-cols-4 gap-4" data-id="element-1077">
          <Card data-id="element-1078">
            <div className="pb-2 pt-4 px-4" data-id="element-1079">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1080">
                Invoices Processed
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1081">
              <div className="flex items-baseline gap-2" data-id="element-1082">
                <div className="text-3xl font-bold" data-id="element-1083">498</div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1084">
                  ↑ 2.3%
                </span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1085">Past 30 Days</div>
            </CardContent>
          </Card>
          <Card data-id="element-1086">
            <div className="pb-2 pt-4 px-4" data-id="element-1087">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1088">
                Pipeline (Active)
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1089">
              <div className="flex items-baseline gap-2" data-id="element-1090">
                <div className="text-3xl font-bold" data-id="element-1091">847,293</div>
                <span className="text-xs text-gray-600 font-medium" data-id="element-1092">
                  → 0.1%
                </span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1093">Receivables to Verify</div>
            </CardContent>
          </Card>
          <Card data-id="element-1094">
            <div className="pb-2 pt-4 px-4" data-id="element-1095">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1096">
                Active Queue
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1097">
              <div className="flex items-baseline gap-2" data-id="element-1098">
                <div className="text-3xl font-bold" data-id="element-1099">8,473</div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1100">
                  ↑ 5.2%
                </span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1101">In Processing</div>
            </CardContent>
          </Card>
          <Card data-id="element-1102">
            <div className="pb-2 pt-4 px-4" data-id="element-1103">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1104">Accuracy</div>
            </div>
            <CardContent className="pt-0" data-id="element-1105">
              <div className="flex items-baseline gap-2" data-id="element-1106">
                <div className="text-3xl font-bold" data-id="element-1107">99.7%</div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1108">
                  ↑ 0.2%
                </span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1109">≥ 0.85 Confidence</div>
            </CardContent>
          </Card>
        </div>
      </div>
      {/* Engine Performance */}
      <div className="mb-6" data-id="element-1110">
        <h2 className="text-lg font-medium text-gray-900 mb-3" data-id="element-1111">
          Engine Performance
        </h2>
        <div className="grid grid-cols-4 gap-4" data-id="element-1112">
          <Card data-id="element-1113">
            <div className="pb-2 pt-4 px-4" data-id="element-1114">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1115">
                Processing Volume
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1116">
              <div className="text-3xl font-bold" data-id="element-1117">$63.4M</div>
              <Sparkline data={sparklineData} data-id="element-1118" />
            </CardContent>
          </Card>
          <Card data-id="element-1119">
            <div className="pb-2 pt-4 px-4" data-id="element-1120">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1121">Throughput</div>
            </div>
            <CardContent className="pt-0" data-id="element-1122">
              <div className="flex items-baseline gap-2" data-id="element-1123">
                <div className="text-3xl font-bold" data-id="element-1124">855 TPS</div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1125">
                  ↑ 1.8%
                </span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1126">Transactions/Second</div>
            </CardContent>
          </Card>
          <Card data-id="element-1127">
            <div className="pb-2 pt-4 px-4" data-id="element-1128">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1129">
                Processing Time
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1130">
              <div className="flex items-baseline gap-2" data-id="element-1131">
                <div className="text-3xl font-bold" data-id="element-1132">36s</div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1133">
                  ↓ 3.1%
                </span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1134">Average Latency</div>
            </CardContent>
          </Card>
          <Card data-id="element-1135">
            <div className="pb-2 pt-4 px-4" data-id="element-1136">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1137">
                Success Rate
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1138">
              <div className="flex items-baseline gap-2" data-id="element-1139">
                <div className="text-3xl font-bold" data-id="element-1140">99.7%</div>
                <span className="text-xs text-gray-600 font-medium" data-id="element-1141">
                  → 0.0%
                </span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1142">≥ 0.85 Confidence</div>
            </CardContent>
          </Card>
        </div>
      </div>
      {/* Agentic Funnel */}
      <div data-id="element-1143">
        <h2 className="text-lg font-medium text-gray-900 mb-3" data-id="element-1144">
          Agentic Funnel
        </h2>
        <div className="grid grid-cols-5 gap-4" data-id="element-1145">
          <Card data-id="element-1146">
            <div className="pb-2 pt-4 px-4" data-id="element-1147">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1148">
                Discovery Agent
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1149">
              <div className="flex items-baseline gap-2" data-id="element-1150">
                <div className="text-2xl font-bold" data-id="element-1151">
                  {(data.agents[0].processed / 1000000).toFixed(2)}M
                </div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1152">↑</span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1153">Invoices Indexed</div>
            </CardContent>
          </Card>
          <Card data-id="element-1154">
            <div className="pb-2 pt-4 px-4" data-id="element-1155">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1156">
                Extraction Agent
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1157">
              <div className="flex items-baseline gap-2" data-id="element-1158">
                <div className="text-2xl font-bold" data-id="element-1159">
                  {Math.round(data.agents[1].processed / 1000)}K
                </div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1160">↑</span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1161">Fields Extracted</div>
            </CardContent>
          </Card>
          <Card data-id="element-1162">
            <div className="pb-2 pt-4 px-4" data-id="element-1163">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1164">
                Validation Agent
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1165">
              <div className="flex items-baseline gap-2" data-id="element-1166">
                <div className="text-2xl font-bold" data-id="element-1167">
                  {Math.round(data.agents[2].processed / 1000)}K
                </div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1168">↑</span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1169">Links Verified</div>
            </CardContent>
          </Card>
          <Card data-id="element-1170">
            <div className="pb-2 pt-4 px-4" data-id="element-1171">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1172">
                Underwriting Agent
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1173">
              <div className="flex items-baseline gap-2" data-id="element-1174">
                <div className="text-2xl font-bold" data-id="element-1175">
                  {Math.round(data.agents[3].processed / 1000)}K
                </div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1176">↑</span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1177">Receivables Scored</div>
            </CardContent>
          </Card>
          <Card data-id="element-1178">
            <div className="pb-2 pt-4 px-4" data-id="element-1179">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1180">
                On-Chain Audit
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1181">
              <div className="flex items-baseline gap-2" data-id="element-1182">
                <div className="text-2xl font-bold" data-id="element-1183">
                  {(data.agents[4].processed / 1000).toFixed(1)}K
                </div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1184">↑</span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1185">Blocks Verified</div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>;
}